<template>
  <PrimeSelect
    ref="primeSelectRef"
    v-bind="mergedAttrs"
    :model-value="modelValue"
    :options="effectiveOptions"
    :filter="effectiveFilter"
    :loading="effectiveLoading"
    :empty-message="primeEmptyMessage"
    :virtual-scroller-options="effectiveVirtualScrollerOptions"
    :data-key="fieldKeys.effectiveDataKey ?? undefined"
    :option-label="fieldKeys.primeOptionLabel ?? undefined"
    :option-value="fieldKeys.primeOptionValue ?? undefined"
    @filter="onFilter"
    @show="onShow"
    @hide="onHide"
    @update:model-value="onUpdateModelValue"
  >
    <template v-if="showRemoteFilter" #header="slotData">
      <slot name="header" v-bind="slotData ?? {}" />
      <GpxSelectFilterInput
        ref="filterInputRef"
        :model-value="remoteFilterQuery"
        :placeholder="filterPlaceholder"
        :aria-owns="filterAriaAttrs.ariaOwns"
        :aria-active-descendant="filterAriaAttrs.ariaActiveDescendant"
        @update:model-value="onRemoteFilterInput"
        @keydown="delegateFilterKeyDown"
        @blur="delegateFilterBlur"
      />
    </template>
    <template v-if="hasOptionSlot" #option="slotData">
      <slot
        name="option"
        v-bind="{
          ...slotData,
          filterQuery: remoteFilterQuery,
          selected: isSelectedOption(slotData?.option),
        }"
      />
    </template>
    <template v-if="remoteEnabled && !slots.empty" #empty>
      {{ effectiveShowEmptyMessage ? remoteEmptyMessage : '' }}
    </template>
    <template v-if="useDefaultValueSlot" #value="slotData">
      <slot
        name="value"
        v-bind="valueSlotProps(slotData?.placeholder)"
      >
        {{ valueSlotProps(slotData?.placeholder).label ?? slotData?.placeholder }}
      </slot>
    </template>
    <template
      v-for="slotName in forwardedSlotNames"
      :key="slotName"
      #[slotName]="slotData"
    >
      <slot :name="slotName" v-bind="slotData ?? {}" />
    </template>
  </PrimeSelect>
</template>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<script setup lang="ts">
import { computed, nextTick, ref, useAttrs, useSlots } from 'vue'
import PrimeSelect from 'primevue/select'
import GpxSelectFilterInput from '../components/GpxSelectFilterInput.vue'
import {
  findOptionByValue,
  getOptionLabel,
  isOptionSelected,
} from '../composables/gpx-select-option-utils'
import { useGpxSelectAttrs } from '../composables/useGpxSelectAttrs'
import { useGpxSelectFieldKeys } from '../composables/useGpxSelectFieldKeys'
import { useGpxSelectPrimeKeyboard } from '../composables/useGpxSelectPrimeKeyboard'
import type { GpxSelectPrimeInstance } from '../composables/useGpxSelectPrimeKeyboard'
import { useGpxSelectState } from '../composables/useGpxSelectState'
import type { GpxRemoteSelectFetchPage } from '../remote-select/types'
import type { OptionLabelKey } from '../composables/gpx-select-option-utils'

const props = withDefaults(
  defineProps<{
    modelValue?: unknown
    options?: unknown[]
    remote?: boolean
    fetchPage?: GpxRemoteSelectFetchPage<unknown> | null
    fetchOptions?: ((args: { query: string }) => Promise<unknown[]>) | null
    perPage?: number
    debounceMs?: number
    itemSize?: number
    valueKey?: string | null
    labelKey?: OptionLabelKey
    optionLabel?: OptionLabelKey
    optionValue?: string | null
    dataKey?: string | null
    getItemId?: ((item: unknown) => string | number) | null
    resolveSelected?: ((value: unknown) => Promise<unknown | null>) | null
  }>(),
  {
    modelValue: undefined,
    options: () => [],
    remote: false,
    fetchPage: undefined,
    fetchOptions: undefined,
    perPage: 100,
    debounceMs: 300,
    itemSize: 38,
    valueKey: undefined,
    labelKey: undefined,
    optionLabel: undefined,
    optionValue: undefined,
    dataKey: undefined,
    getItemId: undefined,
    resolveSelected: undefined,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: unknown]
  filter: [event: { value: string }]
}>()

const attrs = useAttrs()
const slots = useSlots()
const primeSelectRef = ref<GpxSelectPrimeInstance | null>(null)
const filterInputRef = ref<{ focus: () => void } | null>(null)
const hasOptionSlot = computed(() => Boolean(slots.option))

const fieldKeys = useGpxSelectFieldKeys(props, attrs as Record<string, unknown>)

const {
  remoteEnabled,
  effectiveOptions,
  effectiveLoading,
  effectiveShowEmptyMessage,
  effectiveResolvedSelected,
  effectiveVirtualScrollerOptions,
  remoteFilterQuery,
  onFilterHandler,
  onOverlayShowHandler,
  cacheSelected,
} = useGpxSelectState(props, fieldKeys)

const triggerSelectedOption = computed(() => {
  if (props.modelValue == null || props.modelValue === '') {
    return null
  }
  const resolved = effectiveResolvedSelected.value
  if (resolved != null) {
    return resolved
  }
  return findOptionByValue(
    effectiveOptions.value,
    props.modelValue,
    fieldKeys.value.effectiveGetItemId,
  )
})

const triggerSelectedLabel = computed(() => {
  const option = triggerSelectedOption.value
  if (option == null) {
    return null
  }
  return getOptionLabel(option, fieldKeys.value.effectiveLabelKey)
})

const useDefaultValueSlot = computed(() => {
  if (
    remoteEnabled.value &&
    props.modelValue != null &&
    props.modelValue !== ''
  ) {
    return true
  }
  return triggerSelectedOption.value != null
})

const {
  showRemoteFilter,
  filterPlaceholder,
  resetFilterOnHide,
  autoFilterFocus,
  forwardedSlotNames,
  mergedAttrs,
  effectiveFilter,
  remoteEmptyMessage,
} = useGpxSelectAttrs(remoteEnabled, { usesInternalValueSlot: useDefaultValueSlot })

const primeEmptyMessage = computed(() => {
  if (!remoteEnabled.value) {
    return (attrs.emptyMessage ?? attrs['empty-message']) as string | undefined
  }
  return effectiveShowEmptyMessage.value ? remoteEmptyMessage.value : ''
})

const {
  filterAriaAttrs,
  delegateFilterKeyDown,
  delegateFilterBlur,
  resetFocusedOptionOnFilterInput,
} = useGpxSelectPrimeKeyboard(primeSelectRef)

function valueSlotProps(placeholder?: string) {
  return {
    placeholder,
    label: triggerSelectedLabel.value,
    option: triggerSelectedOption.value,
  }
}

function isSelectedOption(option: unknown) {
  return isOptionSelected(
    option,
    props.modelValue,
    fieldKeys.value.effectiveGetItemId,
  )
}

function onUpdateModelValue(value: unknown) {
  if (value != null && value !== '') {
    const option = findOptionByValue(
      effectiveOptions.value,
      value,
      fieldKeys.value.effectiveGetItemId,
    )
    if (option != null) {
      cacheSelected(option)
    }
  } else {
    cacheSelected(null)
  }
  emit('update:modelValue', value)
}

function onFilter(event: { value: string }) {
  onFilterHandler(event)
  emit('filter', event)
}

function onRemoteFilterInput(value: string) {
  onFilter({ value: value ?? '' })
  resetFocusedOptionOnFilterInput()
}

function onShow() {
  onOverlayShowHandler()
  if (showRemoteFilter.value && autoFilterFocus.value) {
    nextTick(() => filterInputRef.value?.focus())
  }
}

function onHide() {
  if (showRemoteFilter.value && resetFilterOnHide.value) {
    onFilter({ value: '' })
  }
}
</script>
